import React, { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Clock, HandCoins } from "lucide-react";
import { api } from "@/lib/api";
import CategoryRFQModal from "@/components/CategoryRFQModal";
import SEO from "@/components/SEO";
import RotatingTestimonial from "@/components/RotatingTestimonial";

// Credibility strip: real supplier logos where we have them, clean wordmarks otherwise.
const TRUSTED = [
  { name: "Setuply Group", src: "" },
  { name: "Bees Global Living", src: "" },
  { name: "Oakley Textiles", src: "/brand/suppliers/oakley-textiles.png" },
];

const absUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export default function GetQuotesPage() {
  const [category, setCategory] = useState(null);
  const [top, setTop] = useState([]);
  const [logos, setLogos] = useState(TRUSTED);

  useEffect(() => {
    api.get("/categories/tree").then(async ({ data }) => {
      const linen = data.find((c) => /linen/i.test(c.name)) || data[0];
      if (!linen) return;
      setCategory(linen);
      const { data: l } = await api.get(`/listings?parent_category_id=${linen.id}&limit=8`);
      setTop(l.items || []);
    }).catch(() => {});
    // Pull live logos for Setuply / Bees from their directory listings when available.
    Promise.all(TRUSTED.filter((t) => !t.src).map(async (t) => {
      try {
        const { data } = await api.get(`/listings?q=${encodeURIComponent(t.name.split(" ")[0])}&limit=3`);
        const hit = (data.items || []).find((i) => (i.company || i.advertiser_name || i.title || "").toLowerCase().includes(t.name.split(" ")[0].toLowerCase()));
        return { ...t, src: absUrl(hit?.logo_url || hit?.image_url || "") };
      } catch { return t; }
    })).then((resolved) => setLogos(TRUSTED.map((t) => resolved.find((r) => r.name === t.name) || t)));
  }, []);

  const proof = useMemo(() => ([
    { icon: HandCoins, t: "Free for buyers, always" },
    { icon: Clock, t: "Quotes typically within 24 hours" },
    { icon: ShieldCheck, t: "Only verified suppliers receive your request" },
  ]), []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="get-quotes-page">
      <SEO title="Get linen quotes from verified suppliers — free, no sign-up" description="One quick form sends your linen request to up to 4 verified hospitality suppliers. Free for buyers, no account needed." path="/get-quotes" noindex />
      <div className="bg-[#11211A] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-28 md:pt-14 md:pb-36">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/60" data-testid="get-quotes-brand">
            <img src="/brand/as-icon-64.png" alt="" className="w-5 h-5" />
            Accommodation Suppliers
          </div>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl" data-testid="get-quotes-headline">
            Get linen quotes from verified suppliers. <span className="text-[#D27C6A]">Free, no sign-up.</span>
          </h1>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/80" data-testid="get-quotes-proof">
            {proof.map(({ icon: Icon, t }) => <div key={t} className="inline-flex items-center gap-2"><Icon className="w-4 h-4 text-[#D27C6A]" /> {t}</div>)}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 md:-mt-24 relative z-10" data-testid="get-quotes-form-wrap">
        {category ? (
          <CategoryRFQModal open inline source="campaign" category={category} topListings={top} />
        ) : (
          <div className="bg-white border border-[#E8ECE9] p-10 text-center text-sm text-[#7D8C85]" data-testid="get-quotes-loading">Loading the quote form…</div>
        )}
      </div>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16" data-testid="get-quotes-trusted">
        <div className="text-center text-[11px] uppercase tracking-[0.3em] text-[#7D8C85]">Trusted by suppliers including</div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {logos.map((l) => (
            <div key={l.name} className="h-24 bg-white border border-[#E8ECE9] flex items-center justify-center px-6" data-testid={`get-quotes-logo-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.src
                ? <img src={l.src} alt={l.name} className="max-h-14 max-w-full object-contain" loading="lazy" />
                : <span className="font-serif text-lg text-[#264839] tracking-tight text-center leading-tight">{l.name}</span>}
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-6" data-testid="get-quotes-testimonial">
        <RotatingTestimonial page="get-quotes" />
      </div>

      <div className="py-8 text-center text-[11px] text-[#7D8C85]" data-testid="get-quotes-legal">
        © {new Date().getFullYear()} Accommodation Suppliers · Your details are shared only with the suppliers you choose.
      </div>
    </div>
  );
}
